import { useEffect, useRef } from "react";
import { motion, useInView, useMotionValue, useSpring } from "framer-motion";
import { Crosshair, ShieldCheck, Cpu, Globe2 } from "lucide-react";
import { Reveal, Eyebrow, SectionTitle } from "./Reveal";
import { IMAGES } from "@/data/content";

const PILLARS = [
  { icon: Crosshair, title: "Execution", text: "Disciplined process over impulse." },
  { icon: ShieldCheck, title: "Discipline", text: "Risk frameworks before returns." },
  { icon: Cpu, title: "Predictive Tech", text: "Data-led research methodology." },
  { icon: Globe2, title: "Sovereignty", text: "Independent, unbiased education." },
];

const Counter = ({ value, suffix, decimals = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { duration: 2, bounce: 0 });
  const display = useRef(null);

  useEffect(() => {
    if (inView) mv.set(value);
  }, [inView, value, mv]);

  useEffect(
    () =>
      spring.on("change", (v) => {
        if (display.current)
          display.current.textContent = v.toFixed(decimals) + suffix;
      }),
    [spring, suffix, decimals]
  );

  return (
    <span ref={ref}>
      <span ref={display}>0{suffix}</span>
    </span>
  );
};

export default function AboutSection() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative overflow-hidden border-y border-line bg-surface py-24"
    >
      <div className="grid-lines pointer-events-none absolute inset-0 opacity-40" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2">
        <Reveal>
          <div className="relative">
            <div className="clip-frame overflow-hidden rounded-xl border border-line">
              <motion.img
                src={IMAGES.aboutHq}
                alt="TEAM X global research terminal — glass atrium of a futuristic research facility"
                loading="lazy"
                initial={{ scale: 1.12 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
                className="aspect-[4/5] w-full object-cover sm:aspect-[5/4]"
              />
            </div>
            <div className="absolute -bottom-6 -right-2 rounded-xl border border-line bg-panel/95 px-6 py-5 backdrop-blur-md sm:-right-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand">
                Capital Analyzed
              </p>
              <p className="mt-1 font-display text-4xl font-black text-ink">
                <Counter value={1.4} suffix="B+" decimals={1} />
              </p>
            </div>
          </div>
        </Reveal>

        <div>
          <Reveal>
            <Eyebrow>About Team X</Eyebrow>
            <SectionTitle className="mt-3">
              Independent Intelligence. <span className="text-brand">Zero Noise.</span>
            </SectionTitle>
            <p className="mt-5 text-sm leading-relaxed text-mute sm:text-base">
              TEAM X is an independent investment education and consulting
              platform that provides market insights, educational content,
              research resources, and consultation services for individuals
              seeking to learn more about modern industries and financial
              markets.
            </p>
          </Reveal>

          <div className="mt-8 grid grid-cols-2 gap-3">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={0.08 * i}>
                <div className="card-trace h-full rounded-lg border border-line bg-panel p-4">
                  <p.icon size={18} className="text-brand" />
                  <h4 className="mt-2.5 font-display text-lg font-bold uppercase tracking-wide text-ink">
                    {p.title}
                  </h4>
                  <p className="mt-1 text-xs leading-relaxed text-mute">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <div className="mt-8 flex flex-wrap gap-x-10 gap-y-4 border-t border-line pt-6">
              <div>
                <p className="font-display text-3xl font-black text-ink">
                  <Counter value={94.2} suffix="%" decimals={1} />
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim">
                  Client Retention
                </p>
              </div>
              <div>
                <p className="font-display text-3xl font-black text-ink">
                  <Counter value={52} suffix="" />
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim">
                  Countries Served
                </p>
              </div>
              <div>
                <p className="font-display text-3xl font-black text-ink">
                  <Counter value={12} suffix="K+" />
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim">
                  Learners Educated
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
